import { useMemo, useState } from "react";
import { Loader2, Pencil, Link2, CheckCircle2, Clock, Mail, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { buildWhatsAppUrl, WhatsAppIcon } from "@/lib/whatsapp";
import { ProsthesisLabDialog } from "./ProsthesisLabDialog";
import { PendingPartnerInvitesList } from "./PendingPartnerInvitesList";
import { ProsthesisLabConnectDialog } from "./ProsthesisLabConnectDialog";
import { ProsthesisLabMessageDialog } from "./ProsthesisLabMessageDialog";
import type { ProsthesisLabRow } from "./types";

export type ConnectionFilter = "" | "conectado" | "pendente";

type Props = {
  onAddNew: () => void;
  connectionFilter?: ConnectionFilter;
  onConnectionFilterChange?: (filter: ConnectionFilter) => void;
};

export function ProsthesisLabsTab({ onAddNew, connectionFilter: controlledFilter, onConnectionFilterChange }: Props) {
  const utils = trpc.useUtils();
  const listQuery = trpc.prosthesis.lab.list.useQuery();
  const allLabs = useMemo(() => (listQuery.data ?? []) as ProsthesisLabRow[], [listQuery.data]);
  const outgoingQuery = trpc.prosthesis.lab.connectionRequests.listOutgoing.useQuery();
  const outgoing = outgoingQuery.data ?? [];
  const cancelRequest = trpc.prosthesis.lab.connectionRequests.cancel.useMutation({
    onSuccess: () => {
      toast.success("Pedido de conexão cancelado.");
      utils.prosthesis.lab.connectionRequests.listOutgoing.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao cancelar."),
  });
  const [editLab, setEditLab] = useState<ProsthesisLabRow | null>(null);
  const [connectLab, setConnectLab] = useState<ProsthesisLabRow | null>(null);
  const [messageLab, setMessageLab] = useState<ProsthesisLabRow | null>(null);
  const [search, setSearch] = useState("");
  const [uncontrolledFilter, setUncontrolledFilter] = useState<ConnectionFilter>("");
  const connectionFilter = controlledFilter ?? uncontrolledFilter;
  const setConnectionFilter = onConnectionFilterChange ?? setUncontrolledFilter;

  const counts = useMemo(
    () => ({
      todos: allLabs.length,
      conectado: allLabs.filter((l) => l.linkedOrganizationId).length,
      pendente: allLabs.filter((l) => !l.linkedOrganizationId).length,
    }),
    [allLabs],
  );

  const labs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allLabs.filter((l) => {
      if (connectionFilter === "conectado" && !l.linkedOrganizationId) return false;
      if (connectionFilter === "pendente" && l.linkedOrganizationId) return false;
      if (term && !l.name.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [allLabs, search, connectionFilter]);

  return (
    <div>
      <PendingPartnerInvitesList />

      {outgoing.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-500/30 dark:bg-amber-500/5">
          <div className="flex items-center justify-between gap-2 border-b border-amber-200/60 px-3 py-2 dark:border-amber-500/20">
            <p className="text-[12px] font-semibold text-amber-900 dark:text-amber-300">
              {outgoing.length} pedido{outgoing.length > 1 ? "s" : ""} de conexão aguardando aceite
              do laboratório
            </p>
          </div>
          <ul className="divide-y divide-amber-200/40 dark:divide-amber-500/15">
            {outgoing.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[12px] sm:flex-nowrap">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                  <Clock className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1 truncate font-semibold text-zinc-900 dark:text-foreground">
                  {r.labOrganization.name}
                </span>
                <button
                  type="button"
                  onClick={() => cancelRequest.mutate({ id: r.id })}
                  disabled={cancelRequest.isPending}
                  className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] font-medium text-zinc-500 transition-colors hover:bg-amber-100 hover:text-red-600 disabled:opacity-50 dark:text-muted-foreground dark:hover:bg-amber-500/15"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancelar
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar parceiro..."
            className="h-11 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:h-9 sm:text-sm dark:border-border dark:bg-card dark:text-foreground"
          />
        </div>

        {/* Mesmo arranjo dos status de /lab/pedidos: os três dividem a largura em partes iguais e
            a fonte se ajusta com `clamp()`, cabendo sem barra de rolagem em qualquer largura. */}
        <div className="flex w-full items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 md:inline-flex md:w-auto dark:border-border dark:bg-muted/40">
          <ConnectionFilterButton
            label="Todos"
            count={counts.todos}
            active={connectionFilter === ""}
            onClick={() => setConnectionFilter("")}
          />
          <ConnectionFilterButton
            label="Conectado"
            count={counts.conectado}
            active={connectionFilter === "conectado"}
            onClick={() => setConnectionFilter("conectado")}
          />
          <ConnectionFilterButton
            label="Pendente"
            count={counts.pendente}
            active={connectionFilter === "pendente"}
            onClick={() => setConnectionFilter("pendente")}
          />
        </div>

        {listQuery.isFetching && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
      </div>

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-border dark:bg-card">
        {listQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
          </div>
        ) : labs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-medium text-zinc-900 dark:text-foreground">
              {search || connectionFilter ? "Nenhum parceiro encontrado" : "Nenhum parceiro cadastrado"}
            </p>
            <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
              {search || connectionFilter
                ? "Tente outro termo ou filtro."
                : "Conecte seus parceiros — busque quem já está na plataforma ou convide por link."}
            </p>
            {!search && !connectionFilter && (
              <button
                type="button"
                onClick={onAddNew}
                className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
              >
                <Plus className="h-4 w-4" />
                Novo parceiro
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-100 bg-zinc-50/50 text-[11px] uppercase tracking-wider text-zinc-500 dark:border-border dark:bg-background/50 dark:text-muted-foreground">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Nome</th>
                <th className="hidden px-2 py-2.5 text-left font-semibold md:table-cell">Contato</th>
                <th className="hidden px-2 py-2.5 text-left font-semibold lg:table-cell">Cidade</th>
                <th className="hidden px-2 py-2.5 text-left font-semibold lg:table-cell">Especialidade</th>
                <th className="px-2 py-2.5 text-left font-semibold">Conexão</th>
                <th className="w-10 px-2 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {labs.map((l) => (
                <tr key={l.id} className="border-b border-zinc-100 dark:border-border/50">
                  <td className="px-3 py-2.5 font-medium text-zinc-900 dark:text-foreground">
                    {l.name}
                  </td>
                  <td className="hidden px-2 py-2.5 text-zinc-700 dark:text-foreground/80 md:table-cell">
                    {l.phone ? (
                      <div className="flex items-center gap-1.5">
                        <span>{l.phone}</span>
                        <a
                          href={buildWhatsAppUrl(l.phone, l.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400"
                          aria-label={`Enviar WhatsApp para ${l.name}`}
                          title="Enviar WhatsApp"
                        >
                          <WhatsAppIcon className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    ) : (
                      "—"
                    )}
                    {l.email && (
                      <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 dark:text-muted-foreground">
                        <span className="truncate">{l.email}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMessageLab(l);
                          }}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-brand-50 hover:text-brand-600 dark:hover:bg-primary/10 dark:hover:text-primary"
                          aria-label={`Enviar e-mail para ${l.name}`}
                          title="Enviar e-mail"
                        >
                          <Mail className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="hidden px-2 py-2.5 text-zinc-700 dark:text-foreground/80 lg:table-cell">
                    {[l.city, l.state].filter(Boolean).join(" — ") || "—"}
                  </td>
                  <td className="hidden px-2 py-2.5 text-zinc-700 dark:text-foreground/80 lg:table-cell">
                    {l.specialty || "—"}
                  </td>
                  <td className="px-2 py-2.5">
                    {l.linkedOrganizationId ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
                        <CheckCircle2 className="h-3 w-3" />
                        Conectado
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConnectLab(l)}
                        className="inline-flex h-7 items-center gap-1 rounded-md border border-brand-200 bg-brand-50 px-2 text-[11px] font-semibold text-brand-700 transition-colors hover:bg-brand-100 dark:border-primary/40 dark:bg-primary/10 dark:text-primary dark:hover:bg-primary/20"
                      >
                        <Link2 className="h-3 w-3" />
                        Conectar
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => setEditLab(l)}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 md:h-7 md:w-7 dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground"
                      aria-label={`Editar cadastro de ${l.name}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ProsthesisLabDialog open={!!editLab} lab={editLab} onClose={() => setEditLab(null)} />
      <ProsthesisLabConnectDialog lab={connectLab} onClose={() => setConnectLab(null)} />
      <ProsthesisLabMessageDialog lab={messageLab} onClose={() => setMessageLab(null)} />
    </div>
  );
}

function ConnectionFilterButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-1.5 text-[clamp(10.5px,3vw,12.5px)] font-semibold transition-colors md:h-8 md:flex-none md:gap-1.5 md:px-2.5 md:text-[12.5px]",
        active
          ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200 dark:bg-primary/15 dark:text-primary dark:ring-primary/30"
          : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
      )}
    >
      {label}
      <span
        className={cn(
          "inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums md:min-w-[18px]",
          active ? "bg-brand-100 text-brand-700 dark:bg-primary/25 dark:text-primary" : "bg-zinc-200 text-zinc-600 dark:bg-muted dark:text-muted-foreground",
        )}
      >
        {count}
      </span>
    </button>
  );
}
